import { parseGridAreas } from './expandGridAreaSpec/parseGridAreas.ts';
import { splitByWeights } from './expandGridAreaSpec/splitByWeights.ts';
import type { GridAreaSpec, WidgetPlacement } from './gridSpec.types.ts';

type GridExtent = {
  /** Total fine columns to fill across the grid width. */
  totalColumns: number;
  /** Total fine rows to fill across the grid height. */
  totalRows: number;
};

/**
 * Expands a {@link GridAreaSpec} into one {@link WidgetPlacement} per cell (distinct token),
 * filling `totalColumns × totalRows` exactly. Placements are returned in the token's
 * first-appearance (row-major) order. Base columns/rows are sized by `colWeights`/`rowWeights`
 * (equal when omitted), and a spanning token's box is the union of its cells' segments.
 */
export const expandGridAreaSpec = (
  spec: GridAreaSpec,
  { totalColumns, totalRows }: GridExtent
): WidgetPlacement[] => {
  const { tokensInOrder, boundsByToken, rowCount, columnCount } = parseGridAreas(spec.areas);

  const colWeights = spec.colWeights ?? Array.from({ length: columnCount }, () => 1);
  const rowWeights = spec.rowWeights ?? Array.from({ length: rowCount }, () => 1);
  if (colWeights.length !== columnCount) {
    throw new Error(
      `colWeights length ${colWeights.length} must equal column count ${columnCount}`
    );
  }
  if (rowWeights.length !== rowCount) {
    throw new Error(`rowWeights length ${rowWeights.length} must equal row count ${rowCount}`);
  }

  const columnSegments = splitByWeights(totalColumns, colWeights);
  const rowSegments = splitByWeights(totalRows, rowWeights);

  return tokensInOrder.map(token => {
    const bounds = boundsByToken.get(token)!;
    const left = columnSegments[bounds.minCol].start;
    const top = rowSegments[bounds.minRow].start;
    const right = columnSegments[bounds.maxCol].start + columnSegments[bounds.maxCol].size;
    const bottom = rowSegments[bounds.maxRow].start + rowSegments[bounds.maxRow].size;
    return { x: left, y: top, w: right - left, h: bottom - top };
  });
};

/** Number of cells (distinct tokens) in a grid spec — its widget capacity. */
export const getGridCapacity = (spec: GridAreaSpec): number =>
  parseGridAreas(spec.areas).tokensInOrder.length;
